<template>
   <div class="tabulation">
      <el-card class="chart-card" v-loading="pageLoading">
         <div style="margin-top: 20px;">
            <el-form ref="ruleFormRef" :model="form" :rules="rules" class="demo-ruleForm" :size="formSize" status-icon>
               <el-form-item label="保障年度" prop="year">
                  <div class="aaaa">
                     <el-input v-model.number="form.year" type="number" placeholder="请输入保障年度（例：2026）" />
                  </div>
               </el-form-item>

               <el-form-item label="保障开始时间" prop="startTime">
                  <div class="aaaa">
                     <el-date-picker v-model="form.startTime" type="datetime" placeholder="请选择保障开始时间"
                        format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss" size="large" />
                  </div>
               </el-form-item>

               <el-form-item label="保障结束时间" prop="endTime">
                  <div class="aaaa">
                     <el-date-picker v-model="form.endTime" type="datetime" placeholder="请选择保障结束时间"
                        format="YYYY-MM-DD HH:mm:ss" value-format="YYYY-MM-DD HH:mm:ss" size="large" />
                  </div>
               </el-form-item>

               <el-form-item label="状态" prop="status">
                  <div class="aaaa">
                     <el-select v-model="form.status" placeholder="请选择状态" clearable>
                        <el-option label="停用" :value="0" style="width:100%; text-align-last: center;" />
                        <el-option label="启用" :value="1" style="width:100%; text-align-last: center;" />
                     </el-select>
                  </div>
               </el-form-item>

               <div class="submit">
                  <el-form-item>
                     <el-button type="primary" :loading="loading" @click="onSubmit">提交</el-button>
                  </el-form-item>
               </div>
            </el-form>
         </div>
      </el-card>
   </div>
</template>

<script setup>
import { nextTick, onActivated, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import qs from "qs";
import { queryPeriodInfo, updatePeriod } from "../../utils/api";

const router = useRouter();
const route = useRoute();
const ruleFormRef = ref(null);
const formSize = ref("large");
const loading = ref(false);
const pageLoading = ref(false);
const periodId = ref("");

const form = reactive({
   year: "",
   startTime: "",
   endTime: "",
   status: "",
});

const resetFormData = () => {
   form.year = "";
   form.startTime = "";
   form.endTime = "";
   form.status = "";
   nextTick(() => {
      ruleFormRef.value && ruleFormRef.value.clearValidate();
   });
};

const pad = (value) => String(value).padStart(2, "0");

const secondToDateTime = (value) => {
   if (!value && value !== 0) return "";
   const date = new Date(Number(value) * 1000);
   if (Number.isNaN(date.getTime())) return "";
   const year = date.getFullYear();
   const month = pad(date.getMonth() + 1);
   const day = pad(date.getDate());
   const hours = pad(date.getHours());
   const minutes = pad(date.getMinutes());
   const seconds = pad(date.getSeconds());
   return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const toSecondTime = (value) => {
   if (!value) return "";
   return Math.floor(new Date(value).getTime() / 1000);
};

const validateEndTime = (rule, value, callback) => {
   if (!value) {
      callback(new Error("请选择保障结束时间"));
      return;
   }
   const startTime = toSecondTime(form.startTime);
   const endTime = toSecondTime(value);
   if (startTime && endTime && endTime <= startTime) {
      callback(new Error("保障结束时间需晚于保障开始时间"));
      return;
   }
   callback();
};

const rules = {
   year: [
      { required: true, message: "请填写保障年度", trigger: "blur" },
   ],
   startTime: [
      { required: true, message: "请选择保障开始时间", trigger: "change" },
   ],
   endTime: [
      { required: true, validator: validateEndTime, trigger: "change" },
   ],
};

const goBack = () => {
   router.push("/safeguardTime");
};

const loadDetail = () => {
   const id = route.query.id || sessionStorage.getItem("mutualInsurancePeriodId");
   if (!id) {
      ElMessage({
         message: "缺少保障期ID",
         type: "error",
         center: true,
      });
      goBack();
      return;
   }

   periodId.value = id;
   sessionStorage.setItem("mutualInsurancePeriodId", id);
   resetFormData();
   pageLoading.value = true;
   queryPeriodInfo(qs.stringify({ id })).then((res) => {
      if (res.data.code === 0) {
         const data = res.data.data || {};
         form.year = data.year || "";
         form.startTime = secondToDateTime(data.startTime);
         form.endTime = secondToDateTime(data.endTime);
         form.status = data.status === 0 || data.status === 1 ? data.status : "";
      } else {
         ElMessage({
            message: res.data.msg || "查询保障期详情失败",
            type: "error",
            center: true,
         });
      }
   }).catch((error) => { if (error?.isAuthExpired) return;
      ElMessage({
         message: "查询保障期详情失败",
         type: "error",
         center: true,
      });
   }).finally(() => {
      pageLoading.value = false;
   });
};

const onSubmit = () => {
   ruleFormRef.value.validate((valid) => {
      if (!valid) {
         return;
      }

      loading.value = true;
      const data = {
         id: Number(periodId.value),
         year: Number(form.year),
         startTime: toSecondTime(form.startTime),
         endTime: toSecondTime(form.endTime),
      };

      if (form.status !== "" && form.status !== undefined && form.status !== null) {
         data.status = Number(form.status);
      }

      updatePeriod(data).then((res) => {
         if (res.data.code === 0) {
            ElMessage({
               message: "提交成功",
               type: "success",
               center: true,
            });
            setTimeout(() => {
               goBack();
            }, 500);
         } else {
            ElMessage({
               message: res.data.msg || "提交失败",
               type: "error",
               center: true,
            });
         }
      }).catch((error) => { if (error?.isAuthExpired) return;
         ElMessage({
            message: "编辑保障期失败",
            type: "error",
            center: true,
         });
      }).finally(() => {
         loading.value = false;
      });
   });
};

onMounted(() => {
   loadDetail();
});

onActivated(() => {
   loadDetail();
});
</script>

<style lang="scss" scoped>
::v-deep .chart-card::-webkit-scrollbar {
   display: none;
}

::v-deep .chart-card {
   height: calc(100vh - 145px);
   overflow: auto;
}

::v-deep .el-button {
   width: 100px;
   height: 40px;
   font-size: 16px;
}

::v-deep .el-form-item__content {
   align-items: flex-end;
}

::v-deep .el-form-item {
   align-items: center;
}

::v-deep .aaaa {
   width: 20%;
}

::v-deep .el-card__body {
   padding: 0;
}

::v-deep .el-main {
   overflow: auto !important;
}

::v-deep .el-card.is-always-shadow {
   width: 95%;
   margin: auto;
   margin-top: 10px;
   padding-left: 20px;
   padding-right: 20px;
   padding-bottom: 20px;
}

::v-deep .tabulation {
   margin-top: 10px;
   display: flex;
   justify-content: center;
   align-items: center;
   margin-bottom: 10px;
   overflow: auto !important;
}
</style>

<style scoped>
::v-deep .el-input {
   height: 44px !important;
}

::v-deep .el-form-item__label {
   width: 125px !important;
   font-weight: bold;
}

::v-deep .submit {
   margin-left: 125px;
}

::v-deep .el-date-editor,
::v-deep .el-select {
   width: 100%;
}
</style>
